// Sprint 20 A5: Intelligent Density Mode Suggestion (DS.3-04)
// Track feature usage. If >12 features/day for 5+ qualifying days on "Easy": suggest "Normal".
// Non-blocking toast. "Don't ask again" for 14 days.
import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "mfm_density_suggestion";
const USAGE_KEY = "mfm_feature_usage";
const DISMISS_DAYS = 14;
const EASY_THRESHOLD = 12;
const POWER_THRESHOLD = 20;
const MIN_QUALIFYING_DAYS = 5;

interface DensitySuggestion {
  shouldSuggest: boolean;
  currentDensity: string;
  suggestedDensity: string;
  reason: string;
}

function normalizeFeatureUsageKey(path: string): string {
  return path.replace(/^\/+/, "");
}

/** Track a feature usage event. Call on every significant user action. */
function trackFeatureUsage(featureName: string): void {
  try {
    const normalizedFeatureName = normalizeFeatureUsageKey(featureName);
    if (!normalizedFeatureName) return;

    const today = new Date().toISOString().slice(0, 10);
    const raw = localStorage.getItem(USAGE_KEY);
    const data: Record<string, string[]> = raw ? JSON.parse(raw) : {};

    const todayFeatures = new Set(data[today] ?? []);
    todayFeatures.add(normalizedFeatureName);
    data[today] = Array.from(todayFeatures);

    // Keep only last 7 days
    const cutoff = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    for (const key of Object.keys(data)) {
      if (key < cutoff) delete data[key];
    }

    localStorage.setItem(USAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage may be unavailable — silently ignore
  }
}

/** Check if a density suggestion should be shown */
function checkDensitySuggestion(currentDensity: string): DensitySuggestion {
  const noSuggestion: DensitySuggestion = { shouldSuggest: false, currentDensity, suggestedDensity: currentDensity, reason: "" };

  try {
    // Check dismiss
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const daysSinceDismiss = (Date.now() - new Date(dismissedAt).getTime()) / 86400000;
      if (daysSinceDismiss < DISMISS_DAYS) return noSuggestion;
    }

    // Get usage data
    const raw = localStorage.getItem(USAGE_KEY);
    if (!raw) return noSuggestion;

    const data: Record<string, string[]> = JSON.parse(raw);
    const dailyCounts = Object.values(data).map((features) => features.length);

    // Need at least MIN_QUALIFYING_DAYS of data
    if (dailyCounts.length < MIN_QUALIFYING_DAYS) return noSuggestion;

    // Count qualifying days (days where features >= threshold) — not just average
    const easyQualifyingDays = dailyCounts.filter((c) => c >= EASY_THRESHOLD).length;
    const powerQualifyingDays = dailyCounts.filter((c) => c >= POWER_THRESHOLD).length;
    const avgFeatures = dailyCounts.reduce((a, b) => a + b, 0) / dailyCounts.length;

    // Easy → Normal suggestion (sustained 12+ features for 5+ days)
    if (currentDensity === "easy" && easyQualifyingDays >= MIN_QUALIFYING_DAYS) {
      return {
        shouldSuggest: true,
        currentDensity,
        suggestedDensity: "normal",
        reason: `You're using ${Math.round(avgFeatures)} features daily across ${easyQualifyingDays} days. Switch to Normal for more at a glance?`,
      };
    }

    // Normal → Power suggestion (sustained 20+ features for 5+ days)
    if (currentDensity === "normal" && powerQualifyingDays >= MIN_QUALIFYING_DAYS) {
      return {
        shouldSuggest: true,
        currentDensity,
        suggestedDensity: "power",
        reason: `You're a power user (${Math.round(avgFeatures)} features/day across ${powerQualifyingDays} days). Switch to Power for maximum density?`,
      };
    }
  } catch {
    // localStorage may be unavailable
  }

  return noSuggestion;
}

/** Dismiss the suggestion for 14 days */
function dismissDensitySuggestion(): void {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    // silently ignore
  }
}

/** React hook: checks on mount and when density changes */
function useIntelligentDensity(currentDensity: string): {
  suggestion: DensitySuggestion;
  dismiss: () => void;
} {
  const [suggestion, setSuggestion] = useState<DensitySuggestion>({
    shouldSuggest: false, currentDensity, suggestedDensity: currentDensity, reason: "",
  });

  const dismiss = useCallback(() => {
    dismissDensitySuggestion();
    setSuggestion((prev) => ({ ...prev, shouldSuggest: false }));
  }, []);

  useEffect(() => {
    const result = checkDensitySuggestion(currentDensity);
    setSuggestion(result);
  }, [currentDensity]);

  return { suggestion, dismiss };
}

export {
  checkDensitySuggestion,
  dismissDensitySuggestion,
  normalizeFeatureUsageKey,
  trackFeatureUsage,
  useIntelligentDensity,
};
